<script setup lang="ts">
import { computed, useId } from 'vue'
import CopyButton from '@/components/CopyButton.vue'
import WikiTextEditor from '@/components/editor/WikiTextEditor.vue'
import type { WikiTextStrategy } from '@/components/wikitext/wiki_text_types'
import {
  COPY_TO_EDIT_HINT,
  goalSectionsPlainText,
  groupSentences,
  replaceSectionText,
  type GoalSection,
  type GoalSectionsLayout
} from './goal_sections'

type SectionRow = { isGroupHeader: true, label: string } | { isGroupHeader: false, section: GoalSection }

const LABEL_CLASSES = 'text-text-sm font-bold uppercase leading-snug tracking-widest text-gray-900'

const props = withDefaults(defineProps<{
  sections: GoalSection[]
  originalSections?: GoalSection[]
  layout?: GoalSectionsLayout
  strategies?: Record<string, WikiTextStrategy>
  contentTitle?: string
  isReadOnly?: boolean
  readOnlyHint?: string
}>(), {
  originalSections: () => [],
  layout: 'stacked',
  strategies: () => ({}),
  contentTitle: '',
  isReadOnly: false,
  readOnlyHint: COPY_TO_EDIT_HINT
})

const emit = defineEmits<{
  'update:sections': [sections: GoalSection[]]
}>()

defineSlots<{
  toolbar?(): unknown
}>()

const labelIdPrefix = useId()
const isRows = computed(() => props.layout === 'rows')

const rows = computed<SectionRow[]>(() =>
  props.sections.flatMap((section, index): SectionRow[] => {
    const startsGroup = Boolean(section.group) && section.group !== props.sections[index - 1]?.group
    const sectionRow: SectionRow = { isGroupHeader: false, section }
    return startsGroup ? [{ isGroupHeader: true, label: section.group! }, sectionRow] : [sectionRow]
  })
)
const allSectionsPlainText = computed(() => goalSectionsPlainText(props.sections))
const groups = computed(() => groupSentences(props.sections))

const sectionClasses = computed(() =>
  isRows.value
    ? 'grid grid-cols-1 gap-0.5 border-t border-gray-300 px-3 py-2 first:border-t-0 md:grid-cols-[11rem_minmax(0,1fr)] md:gap-3'
    : '[&+&]:mt-3 [&+&]:border-t [&+&]:border-gray-300 [&+&]:pt-3'
)
const sectionLabelClasses = computed(() => [LABEL_CLASSES, isRows.value ? 'pt-1' : 'mb-1'])
const groupLabelClasses = computed(() => [LABEL_CLASSES, { 'border-t border-gray-300 bg-gray-100 px-3 py-1.5': isRows.value }])

function labelId(key: string) {
  return `${labelIdPrefix}-${key}`
}

function originalTextFor(key: string) {
  return props.originalSections.find((section) => section.key === key)?.text ?? ''
}

function updateSectionText(key: string, text: string) {
  emit('update:sections', replaceSectionText(props.sections, key, text))
}
</script>

<template>
  <div :class="{ 'rounded-xs border border-gray-300': isRows }" data-testid="goal-sections">
    <template v-for="(row, index) in rows" :key="row.isGroupHeader ? `group-${index}` : row.section.key">
      <div v-if="row.isGroupHeader" :class="groupLabelClasses" data-testid="goal-section-group-label">{{ row.label }}</div>
      <div v-else :class="sectionClasses" role="group" :aria-labelledby="labelId(row.section.key)">
        <div :id="labelId(row.section.key)" :class="sectionLabelClasses" data-testid="goal-section-label">{{ row.section.label }}</div>
        <WikiTextEditor
          format="markdown"
          :wiki-text="row.section.text"
          :reset-value="originalTextFor(row.section.key)"
          show-reset
          :read-only="isReadOnly"
          :read-only-hint="readOnlyHint"
          :label="`${contentTitle} ${row.section.label} statement`"
          :strategies="strategies"
          @update:wiki-text="updateSectionText(row.section.key, $event)"
        />
      </div>
    </template>
    <div class="mt-2 flex flex-wrap items-center gap-1" :class="{ 'border-t border-gray-300 px-3 py-2': isRows }">
      <CopyButton :string-to-copy="allSectionsPlainText" button-text="Copy all" button-theme="grayOutlineSecondary" size="small" />
      <CopyButton
        v-for="group in groups"
        :key="group.label"
        :string-to-copy="group.text"
        :button-text="`Copy ${group.label} as one sentence`"
        button-theme="grayOutlineSecondary"
        size="small"
      />
      <slot name="toolbar" />
    </div>
  </div>
</template>
